import ApiClient from 'api-client/ApiClient'
import store from 'app/store'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import CustomAction from 'core/actions/CustomAction'
import DeleteAction from 'core/actions/DeleteAction'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import { cacheActions } from 'core/caching/cacheReducers'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import { noop } from 'utils/fp'

const { dispatch } = store
const { cinder, nova } = ApiClient.getInstance()

export const volumeTypeActions = ActionsSet.make<DataKeys.VolumeTypes>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.VolumeTypes,
  cacheKey: DataKeys.VolumeTypes,
})

export const listVolumeTypes = volumeTypeActions.add(
  new ListAction<DataKeys.VolumeTypes>(async () => {
    // Cinder's GET /types returns public types + private types the current
    // project has access to, which is the correct tenant-scoped view for
    // both admins and non-admins (PCD-9812).
    return cinder.getVolumeTypes()
  }),
)

export const createVolumeType = volumeTypeActions.add(
  new CreateAction<DataKeys.VolumeTypes, { body }>(async ({ body }) => {
    const created = await cinder.createVolumeType(body)
    return created
    // Workaround - New roles unable to fetch private volume types
    // const volumeTypeId = created.id
    // const newVolumeType = await cinder.getVolumeType(volumeTypeId)
    // return newVolumeType
  }),
)

export const updateVolumeType = volumeTypeActions.add(
  new UpdateAction<
    DataKeys.VolumeTypes,
    {
      id: string
      body: unknown
      keysToDelete: string[]
    }
  >(async ({ id, body, keysToDelete }) => {
    await cinder.updateVolumeType(id, body, keysToDelete)
    const updatedVolumeType = await cinder.getVolumeType(id)
    return updatedVolumeType
  }),
)

export const deleteVolumeType = volumeTypeActions.add(
  new DeleteAction<DataKeys.VolumeTypes, { id: string }>(async ({ id }) => {
    await cinder.deleteVolumeType(id)
  }),
)

export const addVolumeTypeTenant = volumeTypeActions.add(
  new CustomAction<DataKeys.VolumeTypes, { id: string; body?: any }>(
    'addVolumeTypeTenant',
    async ({ id, body }) => {
      return cinder.addVolumeTypeTenant({ id, body })
    },
    (result, { id }) => {
      noop()
    },
  ),
)

export const removeVolumeTypeTenant = volumeTypeActions.add(
  new CustomAction<DataKeys.VolumeTypes, { id: string; body?: any }>(
    'removeVolumeTypeTenant',
    async ({ id, body }) => {
      return cinder.removeVolumeTypeTenant({ id, body })
    },
    (result, { id }) => {
      noop()
    },
  ),
)

export const getVolumeTypeTenants = async ({ id }) => {
  return cinder.getVolumeTypeTenants({ id })
}

export const tenantDefaultVolumeTypeActions = ActionsSet.make<DataKeys.TenantDefaultVolumeTypes>({
  uniqueIdentifier: 'project_id',
  entityName: entityNamesByKey.TenantDefaultVolumeTypes,
  cacheKey: DataKeys.TenantDefaultVolumeTypes,
})

export const listTenantDefaultVolumeType = tenantDefaultVolumeTypeActions.add(
  new ListAction<DataKeys.TenantDefaultVolumeTypes>(async () => {
    return cinder.getDefaultTenantVolumeType()
  }),
)

export const setDefaultVolumeTypeTenant = tenantDefaultVolumeTypeActions.add(
  new UpdateAction<DataKeys.TenantDefaultVolumeTypes, { id: string; body?: any }>(
    async ({ id, body }) => {
      return cinder.setDefaultVolumeType(id, body)
    },
  ),
)

export const allTenantDefaultVolumeTypeListActions =
  ActionsSet.make<DataKeys.AllTenantDefaultVolumeTypes>({
    uniqueIdentifier: 'id',
    entityName: entityNamesByKey.AllTenantDefaultVolumeTypes,
    cacheKey: DataKeys.AllTenantDefaultVolumeTypes,
  })

export const listAllTenantDefaultVolumeType = allTenantDefaultVolumeTypeListActions.add(
  new ListAction<DataKeys.AllTenantDefaultVolumeTypes>(async () => {
    return cinder.getAllDefaultTenantVolumeTypeList()
  }),
)
